import {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  useMemo,
  useRef,
  useEffect
} from 'react';
import { useSocket } from 'socket.io-react-hook';
import { useUpdates } from '../updatesContext';

import { useWebSocketHandlers } from './hooks/useWebSocket';
import { ArbitrageOrderBooksContextProps } from './types';
import {
  arbitrageOrderBooksReducer,
  initialState
} from './reducers/arbitrageOrderBooksReducer';
import { _getToken } from 'src/Config/axiosApi';
import { useHeartbeat } from '../../hooks/useHeartbeat';
import {
  notificationsInitialState,
  notificationsReducer
} from './reducers/notificationsReducer';
import { AUTH_DISABLED } from 'src/App/Providers';

const ArbitrageOrderBooksContext = createContext<
  ArbitrageOrderBooksContextProps | undefined
>(undefined);

export const ArbitrageOrderBooksProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { isSystemRunning } = useUpdates();
  const accessToken = _getToken();
  // Use dev token if auth is disabled
  const effectiveToken = AUTH_DISABLED ? 'dev-local-token' : accessToken;

  const [profitPricesLocalCache, dispatch] = useReducer(
    arbitrageOrderBooksReducer,
    initialState
  );

  const [notificationsLocalCache, dispatchNotification] = useReducer(
    notificationsReducer,
    notificationsInitialState
  );

  const pricesResponse = useMemo(
    () => Array.from(profitPricesLocalCache.values()),
    [profitPricesLocalCache]
  );

  const notifications = useMemo(
    () => Array.from(notificationsLocalCache.values()),
    [notificationsLocalCache]
  );

  // Only connect to WebSocket when system is ON
  // When system is OFF, profits-websocket service is not running
  const { socket } = useSocket(import.meta.env.VITE_PROFITS_WS_URL!, {
    enabled: isSystemRunning && (AUTH_DISABLED || !!accessToken),
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() })
  });

  useHeartbeat(socket);

  const {
    lastMessage,
    authenticate,
    sendSubscribeProfitsMessage,
    notificationsLastMessage
  } = useWebSocketHandlers(socket, effectiveToken);

  const messageStateRef = useRef({
    hasAuthenticated: false,
    hasSubscribedProfits: false
  });
  console.log('λ  - messageStateRef:', messageStateRef);

  useEffect(() => {
    if (!socket.connected) {
      messageStateRef.current = {
        hasAuthenticated: false,
        hasSubscribedProfits: false
      };
    }
  }, [socket.connected]);

  useEffect(() => {
    // Skip token check in dev mode
    if (!AUTH_DISABLED && !accessToken) {
      console.warn("[WS] No access token found; can't connect to WS profits");
      return;
    }

    // Only authenticate when system is running
    if (
      socket.connected &&
      isSystemRunning &&
      !messageStateRef.current.hasAuthenticated
    ) {
      console.log(
        '[WS] - authenticating...',
        AUTH_DISABLED ? '(dev mode)' : ''
      );
      authenticate();
      messageStateRef.current.hasAuthenticated = true;
    }
  }, [socket.connected, authenticate, accessToken, isSystemRunning]);

  useEffect(() => {
    console.log('λ  - useEffect - lastMessage:', lastMessage);
    if (['SNAPSHOT', 'UPDATE', 'DELETE'].includes(lastMessage?.action)) {
      dispatch(lastMessage);
    }
  }, [lastMessage]);

  useEffect(() => {
    console.log('λ  - useEffect - lastMessage:', notificationsLastMessage);
    if (
      ['SNAPSHOT', 'UPDATE', 'DELETE'].includes(
        notificationsLastMessage?.action
      )
    ) {
      dispatchNotification(notificationsLastMessage);
    }
  }, [notificationsLastMessage]);

  const isConnected = socket.connected && isSystemRunning;

  useEffect(() => {
    if (
      isConnected &&
      !messageStateRef.current.hasSubscribedProfits &&
      messageStateRef.current.hasAuthenticated
    ) {
      console.log('[WS] - subscribing to profits...');
      sendSubscribeProfitsMessage();
      messageStateRef.current.hasSubscribedProfits = true;
    }
  }, [isConnected, sendSubscribeProfitsMessage]);

  return (
    <ArbitrageOrderBooksContext.Provider
      value={{
        pricesResponse: pricesResponse || null,
        notifications,
        isConnected
      }}
    >
      {children}
    </ArbitrageOrderBooksContext.Provider>
  );
};

export const useArbitrageOrderBooks = () => {
  const context = useContext(ArbitrageOrderBooksContext);
  if (!context) {
    throw new Error(
      'useArbitrageOrderBooks must be used within an ArbitrageOrderBooksProvider'
    );
  }
  return context;
};
